import type { PromotionClient, PromotionGamesList, UseQueryOverrideOptions } from '../../types'
import type { MaybeRefOrGetter } from 'vue'
import { toValue, useOpenapi, useUserInfo, useCoreConfig, useQuery, useLazyQuery, useMutation } from '#imports'

export interface PlayerOfferBody {
    id: string
}

/**
 * @title usePromotionClient
 * @see Docs https://promotion-stage.spilnu.dk/api#/
 */
export const usePromotionClient = () => {
    const { site } = useCoreConfig()
    const client = useOpenapi<PromotionClient.paths>({
        baseUrl: '/gateway/promotion/api',
        service: 'promotion'
    })

    const useGetGamesList = (list: MaybeRefOrGetter<PromotionGamesList | undefined>, options: UseQueryOverrideOptions = {}) => {
        const enabled = () => !!toValue(list) && toValue(options.enabled ?? true)

        return useLazyQuery(async ({ signal }) => {
            const name = toValue(list)

            if (!name || !enabled()) return

            const { data } = await client.GET(`/v1/public/games/${name}`, {
                signal,
                params: {
                    query: {
                        brand: site.brand as PromotionClient.Brand,
                    }
                }
            })

            return data
        }, {
            ...options,
            key: () => toValue(list) ? `promotion:terms-${toValue(list)}` : 'promotion:no-games-list',
            enabled,
        })
    }

    const useGetSignupOffer = (code: string | undefined, options: UseQueryOverrideOptions = {}) => {
        const { isLoggedIn } = useUserInfo()

        const { enabled = !isLoggedIn.value, lazy = true, server = false } = options

        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/promotions/signup-offers', {
                signal,
                params: {
                    query: {
                        // site.brand may be the 'unknown' fallback; cast to the nominal Brand enum.
                        brand: site.brand as PromotionClient.Brand,
                        code,
                    }
                }
            })

            return data
        }, {
            key: code === undefined ? 'promotionClient:signup-offer' : `promotionClient:signup-offer:${code}`,
            server,
            lazy,
            enabled
        })
    }

    const useActivateOffer = useMutation(async (body: PlayerOfferBody) => {
        return client.PUT('/public/player-offers/{id}/activate', {
            params: {
                path: {
                    id: body.id
                }
            }
        })
    })

    const useCancelOffer = useMutation(async (body: PlayerOfferBody) => {
        return client.PUT('/public/player-offers/{id}/cancel', {
            params: {
                path: {
                    id: body.id
                }
            }
        })
    })

    const useGetNextReclaimable = (options: UseQueryOverrideOptions = {}) => {
        const { enabled, lazy = true, server = false } = options

        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/player-offer-promotion-bonuses/next-reclaimables', {
                signal,
                params: {
                    query: {
                        brand: site.brand,
                    }
                }
            })

            return data?.next_reclaimable ?? null
        }, {
            key: 'promotionClient:next-reclaimable',
            server,
            lazy,
            enabled
        })
    }

    return {
        client,
        useGetGamesList,
        useGetSignupOffer,
        useActivateOffer,
        useCancelOffer,
        useGetNextReclaimable,
    }
}
